import React, { ChangeEvent, useEffect, useState } from 'react'
import moment from 'moment'
import { Cascader, Select } from 'antd'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '@/components/organisms/customer/sales-manager/date-picker'
import { deleteStyle } from '@/constants/layout'
import { EditSalesType, PaymentSettingType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ReservationType } from '@/models/reservation'
import { getRoomCascadeOptionsByReservations } from '@/utils/reservations'
import { initializeSelectBoxStyle } from '@/constants/form'
import { SubjectSelect } from './subject-select'
import { SalesSubjectMasterType } from '@/models/sales-manager/sales'
import { cascaderNameSearch } from '@/libs/cascader-name-search'

const QUANTITY_INPUT_PATTERN = /^-?\d*$/

type UsageDetailSubjectProps = {
  editUsageDetail: EditSalesType
  salesSubjects: SalesSubjectMasterType[]
  salesPayments: PaymentSettingType[]
  changeDate: (date, id) => void
  changeState: (e, id) => void
  changeQuantity: (index: number, quantity: number) => void
  deleteUsageDetailSubject: (id) => void
  advanceCompany: any
  index: number
  confirmedDay: string
  allowClosedDates?: boolean
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservations?: ReservationType[]
  reservation?: ReservationType
}

export const UsageDetailSubject: React.FC<UsageDetailSubjectProps> = ({
  editUsageDetail,
  salesSubjects,
  salesPayments,
  changeDate,
  changeState,
  changeQuantity,
  deleteUsageDetailSubject,
  advanceCompany,
  index,
  confirmedDay,
  allowClosedDates = false,
  accountsReceivableSettings,
  reservations,
  reservation,
}) => {
  const { t } = useTranslation()
  const quantity = editUsageDetail.quantity
  const [quantityDraft, setQuantityDraft] = useState(() => String(quantity))

  useEffect(() => {
    if (!/^-?\d+$/.test(quantityDraft)) return
    if (Number(quantityDraft) === quantity) return
    setQuantityDraft(String(quantity))
  }, [quantity])

  const commitQuantityDraft = () => {
    const num = Number(quantityDraft) || 0
    changeQuantity(index, num)
    setQuantityDraft(String(num))
  }

  const suggestSubjectPrice = (priceOfMaster: number) => {
    changeState(
      ({
        target: {
          name: 'salesSubjectPrice',
          value: `${priceOfMaster || 0}`,
        },
      } as unknown) as ChangeEvent,
      index,
    )
  }

  const newAdvanceCompany = advanceCompany?.paymentCompanies?.map(item => ({
    value: item?.id,
    label: item?.name,
    disabled: !item.currentBalance,
  }))
  const salesPaymentOptions =
    salesPayments?.map(item => {
      if (Object.keys(advanceCompany?.paymentMethod || {}).length && item?.id === advanceCompany?.paymentMethod?.id) {
        return { value: item?.id, label: item?.name, children: newAdvanceCompany }
      } else {
        return { value: item?.id, label: item?.name }
      }
    }) || []

  const roomsOptions = getRoomCascadeOptionsByReservations({ reservation, reservations })

  const displayRender = label => label[label.length - 1]

  const salesPaymentCascaderValue = editUsageDetail.salesPaymentId
    ? editUsageDetail.companyId
      ? [editUsageDetail.salesPaymentId, editUsageDetail.companyId]
      : [editUsageDetail.salesPaymentId]
    : salesPaymentOptions[0]?.value
    ? [salesPaymentOptions[0].value]
    : undefined

  // FIXME
  // eslint-disable-next-line no-prototype-builtins
  if (!editUsageDetail.hasOwnProperty('salesDate')) return null

  return (
    <tr>
      <td>
        <DatePicker
          date={editUsageDetail.salesDate ? moment(editUsageDetail.salesDate) : null}
          setDate={date => changeDate(date, index)}
          confirmedDay={confirmedDay}
          allowClosedDates={allowClosedDates}
        />
      </td>
      <td>
        <SubjectSelect
          onChange={value => {
            changeState({ target: { value: value.subjectMaster.id, name: 'salesSubjectId' } }, index)
            changeState({ target: { value: value.subSubject?.id, name: 'salesSubSubjectId' } }, index)
            suggestSubjectPrice(value.subSubject ? value.subSubject.price : value.subjectMaster.price)
          }}
          defaultValue={editUsageDetail?.salesSubjectName ? editUsageDetail : undefined}
          salesSubjectMasters={salesSubjects}
          isEditMode={true}
        />
      </td>
      <td>
        <Select
          onChange={value => {
            const reservationId = roomsOptions?.find(room => room.value === value)?.reservationId
            changeState({ target: { value: reservationId, name: 'reservationId' } }, index)
            changeState({ target: { value: value, name: 'guestRoomAssignId' } }, index)
          }}
          value={editUsageDetail?.guestRoomAssignId || undefined}
          suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
          placeholder={t('Please select')}
        >
          {roomsOptions.map(room => (
            <Select.Option key={room.value} value={room.value}>
              {room.label}
            </Select.Option>
          ))}
        </Select>
      </td>
      <td>
        <input
          name="salesSubjectPrice"
          type="text"
          value={editUsageDetail.salesSubjectPrice}
          css={unitPriceStyle}
          onChange={e => changeState(e, index)}
          placeholder="0000"
          className="unitPriceStyle"
        />
      </td>
      <td>
        <input
          name="quantity"
          type="text"
          css={smallSelectBoxStyle}
          value={quantityDraft}
          onChange={e => {
            const value = e.target.value
            if (!QUANTITY_INPUT_PATTERN.test(value)) return
            setQuantityDraft(value)
            if (/^-?\d+$/.test(value)) {
              changeQuantity(index, Number(value))
            }
          }}
          onBlur={commitQuantityDraft}
          placeholder="0"
          className="quantityStyle"
        />
      </td>
      <td>
        <input type="text" css={hiddenInputStyle} />
      </td>
      <td>
        <Cascader
          options={salesPaymentOptions}
          expandTrigger="hover"
          className="cascader-customize"
          value={salesPaymentCascaderValue}
          displayRender={displayRender}
          popupClassName="popup-cascader-customize"
          showSearch={cascaderNameSearch}
          onChange={async value => {
            const eventChangeSalesPaymentId = {
              target: { value: value[0], name: 'salesPaymentId' },
            }

            const eventChangeCompanyId = {
              target: { value: value[1] ? value[1] : '', name: 'companyId' },
            }
            await changeState(eventChangeSalesPaymentId, index)
            changeState(eventChangeCompanyId, index)
          }}
          allowClear={false}
          suffixIcon={<span></span>}
        />
      </td>
      <td>
        <select
          name="isPaid"
          css={middleSelectBoxStyle}
          className={!editUsageDetail.isPaid ? 'unpaidStyle' : 'paidStyle'}
          value={editUsageDetail.isPaid ? '1' : '0'}
          onChange={e => changeState(e, index)}
        >
          <option selected={!editUsageDetail.isPaid} value="0">
            {t('Unpaid')}
          </option>
          <option selected={!!editUsageDetail.isPaid} value="1">
            {t('Paid')}
          </option>
        </select>
      </td>
      <td>
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={true}
          suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
          onChange={value => {
            changeState(
              {
                target: { value, name: 'salesAccountsReceivableMasterId' },
              },
              index,
            )
          }}
          value={editUsageDetail.salesAccountsReceivableMasterId}
        >
          <Select.Option value="">-</Select.Option>
          {accountsReceivableSettings.map((accountsReceivable, index) => (
            <Select.Option key={index} value={accountsReceivable.id}>
              {accountsReceivable.name}
            </Select.Option>
          ))}
        </Select>
      </td>
      <td>
        <div css={deleteStyle} onClick={() => deleteUsageDetailSubject(index)}>
          <img src={require('@/static/images/delete_yellow.svg')} />
        </div>
      </td>
    </tr>
  )
}

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
})

const smallSelectBoxStyle = css(selectBoxStyle, {
  width: '100%',
  backgroundPosition: '80% center',
  backgroundImage: 'none',
})

const middleInputStyle = css(selectBoxStyle, {
  backgroundImage: 'none',
  '::placeholder': {
    color: '#ccc',
  },
})

export const unitPriceStyle = css(middleInputStyle, {
  marginLeft: 12,
})

const middleSelectBoxStyle = css(selectBoxStyle, {
  backgroundPosition: 'calc(100% - 8px) center',
})

export const xlargeSelectBoxStyle = css(selectBoxStyle, {
  margin: '0 2% 0 1.5%',
  backgroundPosition: 'calc(100% - 8px) center',
})

const hiddenInputStyle = css(middleInputStyle, { visibility: 'hidden' })
